import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf", fontWeight: "normal" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
    { src: "/fonts/Manrope-Medium.ttf", fontWeight: "medium" },
  ],
});

const COL = {
  empId: 0.7,
  name: 1.6,
  jobTitle: 1.4,
  department: 1.2,
  division: 1.2,
  //   grade:       0.6,
  joinDate: 1.0,
  status: 0.8,
  nationality: 1.0,
  mobile: 1.0,
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 80,
    fontSize: 10,
    fontFamily: "Manrope",
  },
  headerSpace: {
    height: 80,
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    marginBottom: 10,
  },
  header_image: {
    width: "95%",
    height: "100%",
    objectFit: "contain",
  },
  centerDiv: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  smallText: {
    fontSize: 9,
    color: "grey",
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingVertical: 5,
    paddingHorizontal: 8,
    backgroundColor: "#2663EB",
    color: "#ffffff",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 4,
    alignItems: "flex-start",
  },
  cell: {
    fontSize: 8,
  },
  cellBold: {
    fontSize: 8,
    fontWeight: "bold",
  },
  cellGreen: {
    fontSize: 8,
    color: "green",
  },
  cellRed: {
    fontSize: 8,
    color: "red",
  },
  footerSpace: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    height: 60,
  },
  footer_image: {
    width: "100%",
    height: 60,
    objectFit: "cover",
  },
  footerRow: {
    position: "absolute",
    bottom: 88,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "center",
    borderTopWidth: 1,
    borderTopColor: "#ccc",
    paddingTop: 5,
  },
  footer: {
    fontSize: 9,
    color: "grey",
  },
});

const EmployeeListReportDocument = ({ employeeListPrintData }) => {
  console.log("employeeListPrintData:", employeeListPrintData);

  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );

  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;
  const activeEmployees = employeeListPrintData?.filter(
    (e) => e.Active?.toString().toUpperCase() === "YES",
  );

  const inactiveEmployees = employeeListPrintData?.filter(
    (e) => e.Active?.toString().toUpperCase() !== "YES",
  );

  const confirmedCount =
    employeeListPrintData?.filter((e) => e.Status === "Confirmed").length || 0;

  const totalCount = employeeListPrintData?.length || 0;

  // Determine report type
  const isActiveReport =
    activeEmployees?.length > 0 && inactiveEmployees?.length === 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER IMAGE */}
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        {/* TITLE */}
        <View style={styles.centerDiv}>
          <Text style={styles.mainTitle}>
            {isActiveReport ? "Employee Report" : "Inactive Employee Report"}
          </Text>
          <Text style={styles.smallText}>
            Total: {employeeListPrintData?.length || 0} employees
            {/* {"   |   "}Active: {activeCount}
            {"   |   "}Confirmed: {confirmedCount} */}
          </Text>
        </View>

        {/* TABLE HEADER */}
        <View style={styles.tableHeader}>
          <View style={{ flex: COL.empId }}>
            <Text style={styles.cellBold}>Emp ID</Text>
          </View>
          <View style={{ flex: COL.name }}>
            <Text style={styles.cellBold}>Name</Text>
          </View>
          <View style={{ flex: COL.jobTitle }}>
            <Text style={styles.cellBold}>Job Title</Text>
          </View>
          <View style={{ flex: COL.department }}>
            <Text style={styles.cellBold}>Department</Text>
          </View>
          <View style={{ flex: COL.division }}>
            <Text style={styles.cellBold}>Division</Text>
          </View>
          {/* <View style={{ flex: COL.grade }}>
            <Text style={styles.cellBold}>Grade</Text>
          </View> */}
          <View style={{ flex: COL.joinDate }}>
            <Text style={styles.cellBold}>Join Date</Text>
          </View>
          <View style={{ flex: COL.status }}>
            <Text style={styles.cellBold}>Status</Text>
          </View>
          {/* <View style={{ flex: COL.nationality }}>
            <Text style={styles.cellBold}>Nationality</Text>
          </View> */}
          <View style={{ flex: COL.mobile }}>
            <Text style={[styles.cellBold]}>Mobile</Text>
          </View>
        </View>

        {/* TABLE ROWS */}
        {employeeListPrintData?.map((r, idx) => (
          <View key={idx} style={styles.tableRow}>
            <View style={{ flex: COL.empId }}>
              <Text style={styles.cell}>{r.EmpId || "-"}</Text>
            </View>

            <View style={{ flex: COL.name }}>
              <Text style={styles.cellBold}>{r.Name || "-"}</Text>
            </View>

            <View style={{ flex: COL.jobTitle }}>
              <Text style={styles.cell}>{r.JobTitle || "-"}</Text>
            </View>

            <View style={{ flex: COL.department }}>
              <Text style={styles.cell}>{r.Department || "-"}</Text>
            </View>

            <View style={{ flex: COL.division }}>
              <Text style={styles.cell}>{r.Division || "-"}</Text>
            </View>
            {/* 
            <View style={{ flex: COL.grade }}>
              <Text style={styles.cell}>{r.Grade || "-"}</Text>
            </View> */}

            <View style={{ flex: COL.joinDate }}>
              <Text style={styles.cell}>
                {r.JoinDate ? dayjs(r.JoinDate).format("DD-MM-YYYY") : "-"}
              </Text>
            </View>

            <View style={{ flex: COL.status }}>
              <Text
                style={[
                  styles.cell,
                  {
                    color:
                      r.Status === "Confirmed" ||
                      r.Status === "confirmed employee" ||
                      r.Status === "Confirmed Employee"
                        ? "green"
                        : "orange",
                  },
                ]}
              >
                {r.Status === "Confirmed" ||
                r.Status === "confirmed employee" ||
                r.Status === "Confirmed Employee"
                  ? "Confirmed"
                  : r.Status || "-"}
              </Text>
            </View>

            {/* <View style={{ flex: COL.nationality }}>
              <Text style={styles.cell}>{r.Nationality || "-"}</Text>
            </View> */}

            <View style={{ flex: COL.mobile }}>
              <Text style={styles.cell}>
                {r.WorkMobile || r.PersonalMobile || "-"}
              </Text>
            </View>
          </View>
        ))}

        {/* FOOTER IMAGE */}
        <View style={styles.footerSpace} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.footer_image} />
          )}
        </View>

        {/* PAGE NUMBER */}
        <View style={styles.footerRow} fixed>
          <Text
            style={styles.footer}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
            fixed
          />
        </View>
      </Page>
    </Document>
  );
};

export default EmployeeListReportDocument;
